import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useActiveProject } from '@/contexts/ActiveProjectContext';

// Processing stages for different file types
export type ProcessingStage = 
  | 'uploading' 
  | 'extracting' 
  | 'canonicalizing' 
  | 'ready' 
  | 'failed'
  | 'skipped'   // duplicate / soft-skip
  // Analysis stages
  | 'queued'
  | 'analyzing';

export type JobType = 'upload' | 'analysis';

export interface ProcessingJob {
  id: string;
  transcriptId?: string;
  filename: string;
  fileType: 'pdf' | 'docx' | 'txt' | 'audio' | 'analysis';
  jobType: JobType;
  stage: ProcessingStage;
  stageMessage: string;
  progress: number; // 0-100
  error?: string;
  startedAt: Date;
  completedAt?: Date;
  projectId: string;
  projectName?: string;
  pages?: number;
  fileSize?: number;
  audioDurationSeconds?: number;
  estimatedTotalSeconds?: number;
  // For bulk analysis
  analysisTotal?: number;
  analysisCompleted?: number;
  // For smart retry - store original file reference
  retryPayload?: {
    file?: File;
    filePath?: string;
    content?: string;
    title?: string;
  };
  retryCount?: number;
  maxRetries?: number;
}

// Estimation constants (seconds per unit)
const SECONDS_PER_MB_PDF = 15; // ~15 seconds per MB for PDF extraction
const SECONDS_PER_MB_DOCX = 8; // ~8 seconds per MB for DOCX
const SECONDS_PER_MB_TXT = 2; // ~2 seconds per MB for text
const SECONDS_PER_AUDIO_MINUTE = 20; // ~20 seconds processing per minute of audio

export function estimateProcessingTime(
  fileType: ProcessingJob['fileType'],
  fileSize?: number,
  audioDurationSeconds?: number
): number {
  if (fileType === 'audio' && audioDurationSeconds) {
    // Audio: estimate based on duration (roughly 1:3 ratio)
    return Math.ceil((audioDurationSeconds / 60) * SECONDS_PER_AUDIO_MINUTE);
  }
  
  if (!fileSize) return 60; // Default fallback: 1 minute
  
  const fileSizeMB = fileSize / (1024 * 1024);
  
  switch (fileType) {
    case 'pdf':
      return Math.max(10, Math.ceil(fileSizeMB * SECONDS_PER_MB_PDF));
    case 'docx':
      return Math.max(5, Math.ceil(fileSizeMB * SECONDS_PER_MB_DOCX));
    case 'txt':
      return Math.max(3, Math.ceil(fileSizeMB * SECONDS_PER_MB_TXT));
    case 'audio':
      // Fallback for audio without duration: estimate from file size (~1MB per minute)
      return Math.max(15, Math.ceil(fileSizeMB * SECONDS_PER_AUDIO_MINUTE));
    default:
      return 30;
  }
}

export function calculateTimeRemaining(job: ProcessingJob): number | null {
  if (job.stage === 'ready' || job.stage === 'failed') return null;
  if (!job.estimatedTotalSeconds) return null;
  
  const elapsed = (Date.now() - job.startedAt.getTime()) / 1000;
  const progressFraction = job.progress / 100;
  
  if (progressFraction > 0 && progressFraction < 1) {
    // Use progress-based estimation for more accuracy
    const estimatedTotal = elapsed / progressFraction;
    return Math.max(0, Math.ceil(estimatedTotal - elapsed));
  }
  
  // Fallback to initial estimate minus elapsed
  return Math.max(0, Math.ceil(job.estimatedTotalSeconds - elapsed));
}

interface ProcessingQueueContextType {
  jobs: ProcessingJob[];
  activeJobCount: number;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  addJob: (job: Omit<ProcessingJob, 'id' | 'startedAt' | 'progress'>) => string;
  updateJob: (jobId: string, updates: Partial<ProcessingJob>) => void;
  removeJob: (jobId: string) => void;
  completeJob: (jobId: string, transcriptId: string) => void;
  failJob: (jobId: string, error: string) => void;
  skipJob: (jobId: string, reason: string) => void;
  cancelJob: (jobId: string) => void;
  retryJob: (jobId: string) => ProcessingJob | null;
  getJobsForProject: (projectId: string) => ProcessingJob[];
  hasProcessingJobs: (projectId?: string) => boolean;
  isCancelled: (jobId: string) => boolean;
}

// Track cancelled job IDs
const cancelledJobIds = new Set<string>();

const ProcessingQueueContext = createContext<ProcessingQueueContextType | null>(null);

const POLL_INTERVAL_OPEN = 3000; // 3 seconds when drawer open
const POLL_INTERVAL_CLOSED = 10000; // 10 seconds when closed

export function ProcessingQueueProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<ProcessingJob[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { toast } = useToast();
  const { activeProjectId } = useActiveProject();

  // Compute active job count
  const activeJobCount = jobs.filter(j => 
    j.stage !== 'ready' && j.stage !== 'failed' && j.stage !== 'skipped'
  ).length;

  // Open/close drawer
  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setIsDrawerOpen(prev => !prev), []);

  // Add a new job to the queue
  const addJob = useCallback((job: Omit<ProcessingJob, 'id' | 'startedAt' | 'progress'>): string => {
    const id = crypto.randomUUID();
    const estimatedTotalSeconds = estimateProcessingTime(
      job.fileType,
      job.fileSize,
      job.audioDurationSeconds
    );
    const newJob: ProcessingJob = {
      ...job,
      id,
      startedAt: new Date(),
      progress: 0,
      estimatedTotalSeconds,
    };
    console.log('[ProcessingQueue] Adding job:', newJob.filename, newJob.jobType, id);
    setJobs(prev => [newJob, ...prev]);
    return id;
  }, []);

  // Update an existing job
  const updateJob = useCallback((jobId: string, updates: Partial<ProcessingJob>) => {
    setJobs(prev => prev.map(job => 
      job.id === jobId ? { ...job, ...updates } : job
    ));
  }, []);

  // Remove a job from the queue
  const removeJob = useCallback((jobId: string) => {
    setJobs(prev => prev.filter(job => job.id !== jobId));
  }, []);

  // Mark a job as complete
  const completeJob = useCallback((jobId: string, transcriptId: string) => {
    setJobs(prev => prev.map(job => {
      if (job.id === jobId) {
        const completedJob: ProcessingJob = {
          ...job,
          transcriptId,
          stage: 'ready',
          stageMessage: 'Ready',
          progress: 100,
          completedAt: new Date(),
        };
        
        // Show toast notification
        toast({
          title: `${job.filename} is ready`,
          description: 'You can now analyze this source.',
          action: undefined, // CTA could be added here
        });
        
        return completedJob;
      }
      return job;
    }));
  }, [toast]);

  // Mark a job as failed
  const failJob = useCallback((jobId: string, error: string) => {
    setJobs(prev => prev.map(job => 
      job.id === jobId 
        ? { 
            ...job, 
            stage: 'failed' as const, 
            stageMessage: 'Failed', 
            error, 
            progress: 0,
            retryCount: (job.retryCount || 0),
          }
        : job
    ));
  }, []);

  // Mark a job as skipped (soft duplicate / non-error)
  const skipJob = useCallback((jobId: string, reason: string) => {
    setJobs(prev => prev.map(job => 
      job.id === jobId 
        ? { 
            ...job, 
            stage: 'skipped' as const, 
            stageMessage: reason, 
            progress: 100,
            completedAt: new Date(),
          }
        : job
    ));
  }, []);

  // Cancel a job
  const cancelJob = useCallback((jobId: string) => {
    cancelledJobIds.add(jobId);
    setJobs(prev => prev.map(job => 
      job.id === jobId 
        ? { ...job, stage: 'failed' as const, stageMessage: 'Cancelled', error: 'Cancelled by user', progress: 0 }
        : job
    ));
  }, []);

  // Check if a job was cancelled
  const isCancelled = useCallback((jobId: string): boolean => {
    return cancelledJobIds.has(jobId);
  }, []);

  // Retry a failed job - returns the reset job for the caller to process
  const retryJob = useCallback((jobId: string): ProcessingJob | null => {
    let jobToRetry: ProcessingJob | null = null;
    
    setJobs(prev => prev.map(job => {
      if (job.id === jobId && job.stage === 'failed') {
        const maxRetries = job.maxRetries || 3;
        const currentRetries = job.retryCount || 0;
        
        if (currentRetries >= maxRetries) {
          toast({
            variant: 'destructive',
            title: 'Max retries reached',
            description: `${job.filename} has failed ${maxRetries} times. Please try uploading again.`,
          });
          return job;
        }
        
        // Remove from cancelled set if it was cancelled
        cancelledJobIds.delete(jobId);
        
        const resetJob: ProcessingJob = {
          ...job,
          stage: 'uploading',
          stageMessage: 'Retrying...',
          progress: 0,
          error: undefined,
          startedAt: new Date(),
          retryCount: currentRetries + 1,
        };
        
        jobToRetry = resetJob;
        return resetJob;
      }
      return job;
    }));
    
    return jobToRetry;
  }, [toast]);

  // Get jobs for a specific project
  const getJobsForProject = useCallback((projectId: string): ProcessingJob[] => {
    return jobs.filter(job => job.projectId === projectId);
  }, [jobs]);

  // Check if there are processing jobs
  const hasProcessingJobs = useCallback((projectId?: string): boolean => {
    const relevantJobs = projectId 
      ? jobs.filter(j => j.projectId === projectId)
      : jobs;
    return relevantJobs.some(j => j.stage !== 'ready' && j.stage !== 'failed');
  }, [jobs]);

  // Poll for transcript status updates
  useEffect(() => {
    const activeJobs = jobs.filter(j => 
      j.transcriptId && j.stage !== 'ready' && j.stage !== 'failed'
    );

    if (activeJobs.length === 0) return;

    const pollInterval = isDrawerOpen ? POLL_INTERVAL_OPEN : POLL_INTERVAL_CLOSED;

    const pollStatuses = async () => {
      const transcriptIds = activeJobs
        .map(j => j.transcriptId)
        .filter((id): id is string => !!id);

      if (transcriptIds.length === 0) return;

      try {
        const { data, error } = await supabase
          .from('transcripts')
          .select('id, status, has_raw_content')
          .in('id', transcriptIds);

        if (error) {
          console.error('Failed to poll transcript statuses:', error);
          return;
        }

        data?.forEach(transcript => {
          const job = jobs.find(j => j.transcriptId === transcript.id);
          if (!job) return;

          if (transcript.status === 'completed' && transcript.has_raw_content) {
            // Job completed successfully
            setJobs(prev => prev.map(j => 
              j.transcriptId === transcript.id
                ? { 
                    ...j, 
                    stage: 'ready' as const, 
                    stageMessage: 'Ready', 
                    progress: 100,
                    completedAt: new Date(),
                  }
                : j
            ));
          }
        });
      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    const intervalId = setInterval(pollStatuses, pollInterval);
    pollStatuses(); // Run immediately

    return () => clearInterval(intervalId);
  }, [jobs, isDrawerOpen]);

  // Auto-remove completed jobs after 30 seconds
  useEffect(() => {
    const completedJobs = jobs.filter(j => j.stage === 'ready' && j.completedAt);
    
    if (completedJobs.length === 0) return;

    const timeouts = completedJobs.map(job => {
      const elapsed = Date.now() - (job.completedAt?.getTime() || 0);
      const remaining = Math.max(0, 30000 - elapsed);
      
      return setTimeout(() => {
        setJobs(prev => prev.filter(j => j.id !== job.id));
      }, remaining);
    });

    return () => timeouts.forEach(clearTimeout);
  }, [jobs]);

  const value: ProcessingQueueContextType = {
    jobs,
    activeJobCount,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    addJob,
    updateJob,
    removeJob,
    completeJob,
    failJob,
    skipJob,
    cancelJob,
    retryJob,
    getJobsForProject,
    hasProcessingJobs,
    isCancelled,
  };

  return (
    <ProcessingQueueContext.Provider value={value}>
      {children}
    </ProcessingQueueContext.Provider>
  );
}

export function useProcessingQueue() {
  const context = useContext(ProcessingQueueContext);
  if (!context) {
    throw new Error('useProcessingQueue must be used within a ProcessingQueueProvider');
  }
  return context;
}
